import React, { useState, useEffect, useMemo, useRef } from 'react';
import EmailThreadModal from './EmailThreadModal';
import { BookingsPanel, FailedMsgPanel } from './AdminPanels';
import { ConsultationBookingsPanel, WaApiChatPanel } from './WaApiPanels';
import { WeeklyChart, WaQualityBadge } from '../components/WeeklyChart';

const API_URL = '';
const SEWA_DAL = { '9973988635': 'Gunjan-1', '9241165807': 'Gunjan-2' };

const STATUS_CONFIG = {
  interested:     { label: 'Interested',     bg: 'bg-green-600', text: 'text-white' },
  not_interested: { label: 'Not Interested', bg: 'bg-red-600', text: 'text-white' },
  no_response:    { label: 'No Response',    bg: 'bg-gray-600', text: 'text-white' },
  call_back:      { label: 'Call Back',      bg: 'bg-amber-500', text: 'text-white' },
};

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [emailThreadLead, setEmailThreadLead] = useState(null);
  const [stats, setStats] = useState({ total: 0, wa1: 0, wa2: 0, today: 0, wa1_number: '', wa2_number: '', interested: 0, not_interested: 0, no_response: 0, call_back: 0 });
  const [aiUsage, setAiUsage] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [showEmails, setShowEmails] = useState(false);
  const [showBookings, setShowBookings] = useState(false);
  const [showConsults, setShowConsults] = useState(false);
  const [showWaApi, setShowWaApi] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [offerSending, setOfferSending] = useState({});

  const sendOffer = async (leadId, template) => {
    const label = template === 'consult_call_5100' ? '₹5100 Call Offer' : '₹51000 Visit Offer';
    setOfferSending(prev => ({ ...prev, [leadId + template]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/wa-offer/${leadId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template }),
      });
      const data = await res.json();
      if (data.success) alert(`✓ ${label} WhatsApp पर भेज दिया गया`);
      else alert(`✗ नहीं भेजा गया: ${data.error || data.raw || 'template approval pending हो सकता है'}`);
    } catch (e) { alert('Network error: ' + e.message); }
    setOfferSending(prev => ({ ...prev, [leadId + template]: false }));
  };
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailDaily, setEmailDaily] = useState(null);
  const [emailPreviews, setEmailPreviews] = useState([]);
  const [showPreviews, setShowPreviews] = useState(false);
  const [openPreviewKey, setOpenPreviewKey] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayDetailLogs, setDayDetailLogs] = useState([]);

  const loadDayDetail = async (date) => {
    if (dayDetail === date) { setDayDetail(null); return; }
    setDayDetail(date);
    setDayDetailLogs([]);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-logs?limit=500&date=${date}`);
      const data = await res.json();
      setDayDetailLogs(data.logs || []);
    } catch (err) { console.error('Day detail load failed', err); }
  };

  useEffect(() => {
    if (!showEmails) return;
    const t = setInterval(() => {
      fetch(`${API_URL}/api/admin/email-daily-stats?days=14`)
        .then(r => r.json()).then(d => setEmailDaily(d)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [showEmails]);

  const loadEmailPanel = async () => {
    setEmailLogsLoading(true);
    try {
      const [logsRes, statusRes, dailyRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/email-logs?limit=150`),
        fetch(`${API_URL}/api/admin/email-status`),
        fetch(`${API_URL}/api/admin/email-daily-stats?days=14`),
      ]);
      const logsData = await logsRes.json();
      const statusData = await statusRes.json();
      const dailyData = await dailyRes.json();
      setEmailLogs(logsData.logs || []);
      setEmailStatus(statusData);
      setEmailDaily(dailyData);
    } catch (err) { console.error('Email logs load failed', err); }
    setEmailLogsLoading(false);
  };

  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);

  const runEmailBackfill = async () => {
    if (!window.confirm('सभी पुराने unpaid customers (email वाले) को report + follow-up automation में डालें? Reports हर 2 min में एक-एक करके जाएंगी।')) return;
    setBackfillLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-backfill-all`, { method: 'POST' });
      const data = await res.json();
      setBackfillResult(data);
    } catch (err) { setBackfillResult({ error: String(err) }); }
    setBackfillLoading(false);
  };

  const togglePreviews = async () => {
    const next = !showPreviews;
    setShowPreviews(next);
    if (next && emailPreviews.length === 0) {
      try {
        const res = await fetch(`${API_URL}/api/admin/email-previews`);
        const data = await res.json();
        setEmailPreviews(data.previews || []);
      } catch (err) { console.error('Previews load failed', err); }
    }
  };
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [phoneLookups, setPhoneLookups] = useState({});
  const [lookupLoading, setLookupLoading] = useState({});
  const [retryCounts, setRetryCounts] = useState({});
  const [statusUpdating, setStatusUpdating] = useState({});
  const [exporting, setExporting] = useState(false);
  const [notes, setNotes] = useState({});
  const [noteSaving, setNoteSaving] = useState({});
  const retryTimersRef = useRef({});

  const [emailStats, setEmailStats] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes, emailStatsRes] = await Promise.all([
        fetch(`${API_URL}/api/contacts`),
        fetch(`${API_URL}/api/admin/stats`),
        fetch(`${API_URL}/api/admin/email-stats`)
      ]);
      fetch(`${API_URL}/api/admin/ai-usage`).then(r => r.json()).then(setAiUsage).catch(() => {});
      const leadsData = await leadsRes.json();
      setLeads(leadsData);
      setStats(await statsRes.json());
      setEmailStats(await emailStatsRes.json());
      // Load notes from leads
      const notesObj = {};
      leadsData.forEach(lead => {
        if (lead.admin_note) notesObj[lead.id] = lead.admin_note;
      });
      setNotes(notesObj);
      
      // Load saved phone details from leads
      const savedLookups = {};
      leadsData.forEach(lead => {
        if (lead.mobile1_details) {
          savedLookups[lead.mobile1] = { status: true, data: [lead.mobile1_details], saved: true };
        }
        if (lead.mobile2_details) {
          savedLookups[lead.mobile2] = { status: true, data: [lead.mobile2_details], saved: true };
        }
      });
      setPhoneLookups(savedLookups);
    } catch (err) {
      console.error('Failed to fetch:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    return () => {
      Object.values(retryTimersRef.current).forEach(t => clearTimeout(t));
    };
  }, []);

  const exportToExcel = () => {
    setExporting(true);
    try {
      const headers = ['#', 'नाम', 'Mobile 1', 'Mobile 2', 'Email', 'समस्या', 'WhatsApp Number', 'Status', 'Note', 'Date'];
      const rows = filteredLeads.map((lead, i) => [
        i + 1, lead.name || '', lead.mobile1 || '', lead.mobile2 || '', lead.email || '',
        lead.problem || '', lead.whatsapp_target || '', lead.call_status || 'Pending',
        notes[lead.id] || lead.admin_note || '',
        lead.created_at ? new Date(lead.created_at).toLocaleDateString('hi-IN') : ''
      ]);
      const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) { alert('Export failed!'); }
    setExporting(false);
  };

  const filteredLeads = useMemo(() => {
    let filtered = leads;
    if (activeTab.startsWith('wa')) filtered = filtered.filter(l => l.whatsapp_target === stats[`${activeTab}_number`]);
    if (activeTab === 'parchi_pending') filtered = filtered.filter(l => l.chat_status === 'offer_made' && l.payment_status !== 'paid');
    if (statusFilter !== 'all') {
      filtered = statusFilter === 'pending' ? filtered.filter(l => !l.call_status) : filtered.filter(l => l.call_status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(l => l.name?.toLowerCase().includes(q) || l.mobile1?.includes(q) || l.mobile2?.includes(q) || l.problem?.toLowerCase().includes(q));
    }
    return filtered;
  }, [leads, activeTab, statusFilter, search, stats]);

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('hi-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

  const hasValidData = (result) => result?.status && result?.data?.length > 0 && result.data.some(e => e.name || e.father_name || e['father name'] || e.fname || e.address);

  const fetchPhoneLookup = async (number, leadId) => {
    // Once fetched (successful or not), don't fetch again. Lookups are PERMANENT.
    if (!number || phoneLookups[number]) return;
    
    setLookupLoading(prev => ({ ...prev, [number]: true }));
    try {
      // Pass lead_id to save details in backend permanently
      const url = leadId ? `${API_URL}/api/phone-lookup/${number}?lead_id=${leadId}` : `${API_URL}/api/phone-lookup/${number}`;
      const res = await fetch(url);
      const result = await res.json();
      // Mark as saved so it never re-fetches even if no data
      setPhoneLookups(prev => ({ ...prev, [number]: { ...result, saved: true } }));
    } catch (err) {
      setPhoneLookups(prev => ({ ...prev, [number]: { status: false, error: 'Failed', data: [], saved: true } }));
    }
    setLookupLoading(prev => ({ ...prev, [number]: false }));
  };

  const toggleExpand = (leadId, mobile1, mobile2) => {
    if (expandedLead === leadId) { setExpandedLead(null); return; }
    setExpandedLead(leadId);
    // Fetch only if never fetched before for this number
    if (mobile1 && !phoneLookups[mobile1]) fetchPhoneLookup(mobile1, leadId);
    if (mobile2 && !phoneLookups[mobile2]) fetchPhoneLookup(mobile2, leadId);
  };

  const updateStatus = async (leadId, newStatus) => {
    setStatusUpdating(prev => ({ ...prev, [leadId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/contacts/${leadId}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, call_status: newStatus } : l));
        setStats(prev => {
          const old = leads.find(l => l.id === leadId)?.call_status;
          const updated = { ...prev };
          if (old && updated[old] > 0) updated[old]--;
          if (newStatus) updated[newStatus] = (updated[newStatus] || 0) + 1;
          return updated;
        });
      }
    } catch (err) { console.error('Status update failed:', err); }
    setStatusUpdating(prev => ({ ...prev, [leadId]: false }));
  };

  const [callingLead, setCallingLead] = useState({});
  const placeCall = async (leadId) => {
    setCallingLead(prev => ({ ...prev, [leadId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/call/${leadId}`, { method: 'POST' });
      const data = await res.json();
      const st = data.success ? 'initiated' : 'failed';
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ai_call_status: st } : l));
      alert(data.success ? '📞 AI call शुरू — सौम्या श्री सेविका कॉल कर रही हैं' : `Call fail: ${data.error || 'unknown'}`);
    } catch (err) { alert('Call failed: ' + err.message); }
    setCallingLead(prev => ({ ...prev, [leadId]: false }));
  };

  const [emailSending, setEmailSending] = useState({});
  const sendEmail = async (leadId, type) => {
    const key = `${leadId}-${type}`;
    setEmailSending(prev => ({ ...prev, [key]: true }));
    try {
      const endpoints = {
        instant: 'send-instant-email',
        report: 'send-report-now',
        payment: 'send-payment-email',
      };
      const res = await fetch(`${API_URL}/api/admin/${endpoints[type]}/${leadId}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert(`✓ ${type === 'instant' ? 'Instant' : type === 'report' ? 'Report' : 'Payment'} email भेज दिया गया → ${data.email}`);
      } else {
        const reason = data.error || 'Unknown error';
        alert(`✗ Email नहीं भेजा गया\n\nReason: ${reason}\n\n👉 अगर "domain is not verified" लिखा है तो production पर पुराना RESEND_API_KEY है — Redeploy करें।\n👉 Admin के 📧 Emails tab में Domain badge "verified" होना चाहिए।`);
      }
    } catch (err) {
      alert(`✗ Error: ${err.message}`);
    }
    setEmailSending(prev => ({ ...prev, [key]: false }));
  };

  const saveNote = async (leadId, noteText) => {    setNoteSaving(prev => ({ ...prev, [leadId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/contacts/${leadId}/note`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_note: noteText })
      });
      const data = await res.json();
      if (data.success) {
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, admin_note: noteText } : l));
      }
    } catch (err) { console.error('Note save failed:', err); }
    setNoteSaving(prev => ({ ...prev, [leadId]: false }));
  };

  const pendingCount = stats.total - (stats.interested + stats.not_interested + stats.no_response + stats.call_back);

  return (
    <div className="min-h-screen bg-slate-50 pb-16" style={{fontFamily: 'system-ui, -apple-system, sans-serif'}}>
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-30 border-b-2 border-blue-500">
        <div className="px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-base sm:text-xl font-bold text-gray-900 whitespace-nowrap" data-testid="admin-title">📊 Admin Dashboard</h1>
          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
            <a href="/maliha-gupt-dwar-J2ipm3nqQUoUxrmse-rziroFzhFbeWTFeDkX-M6reQY/paid" className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-emerald-600 text-white text-xs sm:text-sm font-bold rounded-lg shadow hover:bg-emerald-700 inline-flex items-center whitespace-nowrap" data-testid="paid-leads-link">
              💰 Paid</a>
            <button onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/api/admin/run-nurture-now`, { method: 'POST' });
                const data = await res.json();
                if (data.success) alert(`✓ Automation triggered\nInbox: ${data.inbox_processed} replies processed`);
                else alert('Error: ' + (data.error || 'Failed'));
              } catch (e) { alert('Failed: ' + e.message); }
            }} className="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white text-xs sm:text-sm font-bold rounded-lg shadow hover:bg-purple-700 whitespace-nowrap" data-testid="run-nurture-btn">
              ⚡ Automation
            </button>
            <button onClick={async () => {
              if (!window.confirm('सभी leads के पुराने AI reports clear कर दें? (नया ₹5500 वाला अगली बार generate होगा)')) return;
              try {
                const res = await fetch(`${API_URL}/api/admin/reset-ai-reports`, { method: 'POST' });
                const data = await res.json();
                if (data.success) alert(`✓ ${data.cleared_count} leads के AI reports clear हो गए`);
                else alert('Error: ' + (data.error || 'Failed'));
              } catch (e) { alert('Failed: ' + e.message); }
            }}
              className="px-4 py-2 bg-amber-600 text-white text-sm font-bold rounded-lg shadow hover:bg-amber-700" data-testid="reset-ai-btn">
              ♻ Reset AI
            </button>
            <button onClick={exportToExcel} disabled={exporting || filteredLeads.length === 0}
              className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-lg shadow hover:bg-green-700 disabled:opacity-50" data-testid="export-btn">
              {exporting ? '...' : '📥 Excel Download'}
            </button>
            <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg shadow hover:bg-blue-700" data-testid="refresh-btn">
              🔄 Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="px-4 py-4">
        {/* Email & Payment Stats */}
        {emailStats && (
          <div className="grid grid-cols-5 gap-1 sm:gap-3 mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-2.5 sm:p-4 shadow-lg" data-testid="email-stats-section">
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-white">{emailStats.total_leads || 0}</div>
              <div className="text-[9px] sm:text-xs text-purple-200 font-bold">Total Leads</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-green-300">{emailStats.instant_emails_sent || 0}</div>
              <div className="text-[9px] sm:text-xs text-purple-200 font-bold">✉ Instant Email</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-yellow-300">{emailStats.report_emails_sent || 0}</div>
              <div className="text-[9px] sm:text-xs text-purple-200 font-bold">◈ Report Sent</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-emerald-300">{emailStats.payments_received || 0}</div>
              <div className="text-[9px] sm:text-xs text-purple-200 font-bold">◈ Paid</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-black text-red-300">{emailStats.pending_payments || 0}</div>
              <div className="text-[9px] sm:text-xs text-purple-200 font-bold">◷ Pending</div>
            </div>
          </div>
        )}
        
        {/* Stats Grid - 2x4 grid */}
        <div className="grid grid-cols-4 gap-1.5 sm:gap-3 mb-4" data-testid="stats-section">
          <StatBox value={stats.total} label="कुल Leads" color="bg-blue-600" />
          <StatBox value={stats.today} label="आज के" color="bg-emerald-600" />
          {(stats.members || []).map((m, i) => (
            <StatBox key={m.number} value={m.count} label={m.name} color={['bg-yellow-600','bg-orange-600','bg-teal-600','bg-pink-600','bg-indigo-600'][i % 5]} />
          ))}          <StatBox value={stats.interested} label="Interested" color="bg-green-600" />
          <StatBox value={stats.not_interested} label="Not Interest" color="bg-red-600" />
          <StatBox value={stats.no_response} label="No Response" color="bg-gray-600" />
          <StatBox value={stats.call_back} label="Call Back" color="bg-amber-500" />
        </div>

        {/* WhatsApp quality rating — ban risk warning */}
        <WaQualityBadge />

        {/* 7-day Leads vs Payments chart */}
        <WeeklyChart />

        {/* AI Usage Meter - Free vs Fallback */}
        {aiUsage && (
          <div className="mb-4 p-3 rounded-xl bg-white border border-gray-200 shadow-sm" data-testid="ai-usage-meter">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm font-bold text-gray-700">🤖 AI Usage (14 दिन)</span>
              <div className="flex gap-2">
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold" data-testid="ai-free-count">FREE (ox-alpha): {aiUsage.total_free}</span>
                <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold" data-testid="ai-fallback-count">Fallback (paid): {aiUsage.total_fallback}</span>
                <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">
                  बचत: {aiUsage.total_free + aiUsage.total_fallback > 0 ? Math.round((aiUsage.total_free / (aiUsage.total_free + aiUsage.total_fallback)) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Filters - Scrollable */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {[{ id: 'all', label: `All (${stats.total})` }, { id: 'parchi_pending', label: `🟡 पर्ची Pending (${leads.filter(l => l.chat_status === 'offer_made' && l.payment_status !== 'paid').length})` }, ...(stats.members || []).map((m, i) => ({ id: `wa${i + 1}`, label: `${m.name} (${m.count || 0})` }))].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg whitespace-nowrap shadow ${activeTab === t.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-400'}`}
              data-testid={`tab-${t.id}`}>{t.label}</button>
          ))}
          <span className="text-gray-300 self-center">|</span>
          <button onClick={() => { const next = !showEmails; setShowEmails(next); if (next) loadEmailPanel(); }}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg whitespace-nowrap shadow ${showEmails ? 'bg-purple-600 text-white' : 'bg-white text-purple-700 border-2 border-purple-200 hover:border-purple-400'}`}
            data-testid="tab-emails">📧 Emails</button>
          <button onClick={() => { setShowBookings(!showBookings); setShowEmails(false); setShowFailed(false); }}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg whitespace-nowrap shadow ${showBookings ? 'bg-amber-600 text-white' : 'bg-white text-amber-700 border-2 border-amber-200 hover:border-amber-400'}`}
            data-testid="tab-bookings">📅 Bookings</button>
          <button onClick={() => { setShowFailed(!showFailed); setShowEmails(false); setShowBookings(false); setShowConsults(false); setShowWaApi(false); }}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg whitespace-nowrap shadow ${showFailed ? 'bg-red-600 text-white' : 'bg-white text-red-700 border-2 border-red-200 hover:border-red-400'}`}
            data-testid="tab-failed">⚠️ Failed</button>
          <button onClick={() => { setShowConsults(!showConsults); setShowEmails(false); setShowBookings(false); setShowFailed(false); setShowWaApi(false); }}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg whitespace-nowrap shadow ${showConsults ? 'bg-rose-600 text-white' : 'bg-white text-rose-700 border-2 border-rose-200 hover:border-rose-400'}`}
            data-testid="tab-consults">📞 Consultations</button>
          <button onClick={() => { setShowWaApi(!showWaApi); setShowEmails(false); setShowBookings(false); setShowFailed(false); setShowConsults(false); }}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-bold rounded-lg whitespace-nowrap shadow ${showWaApi ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 border-2 border-emerald-200 hover:border-emerald-400'}`}
            data-testid="tab-wa-api">📲 WhatsApp API</button>
          <span className="text-gray-300 self-center">|</span>
          {[{ id: 'all', label: 'All' }, { id: 'pending', label: `Pending(${pendingCount})` }, { id: 'interested', label: `✓ Interest(${stats.interested})` }, { id: 'not_interested', label: `✗ Not(${stats.not_interested})` }, { id: 'no_response', label: `📵 NoResp(${stats.no_response})` }, { id: 'call_back', label: `☎ Callback(${stats.call_back})` }].map(f => (
            <button key={f.id} onClick={() => setStatusFilter(f.id)}
              className={`px-3 py-2 text-sm font-bold rounded-lg whitespace-nowrap shadow ${statusFilter === f.id ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-gray-400'}`}
              data-testid={`status-filter-${f.id}`}>{f.label}</button>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 नाम, नंबर, या समस्या से खोजें..."
            className="flex-1 px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 shadow-sm" data-testid="search-input" />
          <span className="text-sm text-gray-500 font-bold whitespace-nowrap">{filteredLeads.length} leads</span>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12 bg-white rounded-xl shadow">
            <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 text-base mt-3">Loading...</p>
          </div>
        )}

        {/* Email Delivery Report Panel */}
        {showEmails && (
          <div className="mb-4 bg-white rounded-xl shadow p-4" data-testid="email-report-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-lg font-bold text-gray-800">📧 Email Delivery Report</h3>
              <div className="flex items-center gap-2">
                {emailStatus && (
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${emailStatus.status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="resend-domain-status">
                    Domain: {emailStatus.domain} — {emailStatus.status === 'verified' ? '✓ Verified' : `⏳ ${emailStatus.status}`}
                  </span>
                )}
                <button onClick={runEmailBackfill} disabled={backfillLoading} className="px-3 py-1 text-xs font-bold bg-green-700 text-white rounded-lg disabled:opacity-50" data-testid="email-backfill-btn">{backfillLoading ? '⏳...' : '🔄 पुराने सभी Customers को Automation में डालें'}</button>
                <button onClick={togglePreviews} className={`px-3 py-1 text-xs font-bold rounded-lg ${showPreviews ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800 border border-amber-300'}`} data-testid="email-previews-toggle-btn">📨 Email Previews</button>
                <button onClick={loadEmailPanel} className="px-3 py-1 text-xs font-bold bg-blue-600 text-white rounded-lg" data-testid="email-logs-refresh-btn">↻ Refresh</button>
              </div>
            </div>

            {backfillResult && (
              <div className={`mb-3 p-3 rounded-lg text-sm ${backfillResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="email-backfill-result">
                {backfillResult.success
                  ? <>✅ <b>{backfillResult.scheduled}</b> customers automation में डाल दिए गए — पहली report {backfillResult.first_send_in} में, फिर {backfillResult.spacing}। {backfillResult.note}</>
                  : <>✗ {backfillResult.error || 'कुछ गलत हुआ'}</>}
              </div>
            )}

            {emailDaily && emailDaily.days && (
              <div className="mb-4" data-testid="email-daily-stats-panel">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h4 className="text-sm font-bold text-gray-700">📊 Daily Email Stats (14 दिन) <span className="text-green-600 text-xs font-bold animate-pulse">● LIVE</span></h4>
                  <span className="text-xs text-gray-500">कुल: <b className="text-green-700">{emailDaily.totals?.sent || 0} sent</b> · <b className="text-blue-700">{emailDaily.totals?.opened || 0} opened</b>{emailDaily.totals?.failed ? <> · <b className="text-red-600">{emailDaily.totals.failed} failed</b></> : null}</span>
                  {emailDaily.automation && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-300" data-testid="daily-automation-status">
                      🟢 Daily Automation ON — {emailDaily.automation.queue} customers queue में · {emailDaily.automation.reminders_sent} reminders जा चुके ({emailDaily.automation.leads_in_reminders} customers को)
                    </span>
                  )}
                </div>
                {emailDaily.days.length === 0 ? (
                  <div className="text-xs text-gray-400 py-2">पिछले 14 दिन में कोई email नहीं</div>
                ) : (
                  <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-100 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr className="text-left text-gray-500 uppercase">
                          <th className="p-2">तारीख</th><th className="p-2 text-center">Sent</th><th className="p-2 text-center">Opened</th><th className="p-2 text-center">Open %</th><th className="p-2 text-center">Failed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailDaily.days.map((d) => (
                          <React.Fragment key={d.date}>
                            <tr onClick={() => loadDayDetail(d.date)} className={`border-t border-gray-100 cursor-pointer hover:bg-amber-50 ${dayDetail === d.date ? 'bg-amber-50' : ''}`} data-testid={`email-stat-row-${d.date}`}>
                              <td className="p-2 font-semibold text-gray-700 whitespace-nowrap">{new Date(d.date + 'T00:00:00').toLocaleDateString('hi-IN', { day: '2-digit', month: 'short' })} <span className="text-gray-400">{dayDetail === d.date ? '▲' : '▼'}</span></td>
                              <td className="p-2 text-center font-bold text-green-700">{d.sent}</td>
                              <td className="p-2 text-center font-bold text-blue-700">{d.opened}</td>
                              <td className="p-2 text-center text-gray-600">{d.sent ? Math.round((d.opened / d.sent) * 100) : 0}%</td>
                              <td className="p-2 text-center text-red-600">{d.failed || 0}</td>
                            </tr>
                            {dayDetail === d.date && (
                              <tr className="border-t border-amber-200 bg-amber-50/60">
                                <td colSpan={5} className="p-2" data-testid={`email-day-detail-${d.date}`}>
                                  {dayDetailLogs.length === 0 ? (
                                    <div className="text-gray-400 text-center py-2">इस दिन कोई email नहीं गई</div>
                                  ) : (
                                    <table className="w-full text-xs bg-white rounded border border-amber-200">
                                      <thead><tr className="text-left text-gray-500 uppercase bg-amber-100/60">
                                        <th className="p-1.5">समय</th><th className="p-1.5">किसको</th><th className="p-1.5">Subject</th><th className="p-1.5">Status</th><th className="p-1.5">खुला?</th>
                                      </tr></thead>
                                      <tbody>
                                        {dayDetailLogs.map((l, j) => (
                                          <tr key={l.id || j} className="border-t border-gray-100">
                                            <td className="p-1.5 text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                                            <td className="p-1.5 font-semibold text-gray-800">{l.to}</td>
                                            <td className="p-1.5 text-gray-600 max-w-xs truncate" title={l.subject}>{l.subject}</td>
                                            <td className="p-1.5">{l.status === 'sent' ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-600 font-bold" title={l.error}>✗</span>}</td>
                                            <td className="p-1.5">{l.opened ? <span className="text-blue-600 font-bold">👁 {l.opened_at ? new Date(l.opened_at).toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' }) : 'हाँ'}</span> : <span className="text-gray-400">नहीं</span>}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {showPreviews && (
              <div className="mb-4 border border-amber-200 rounded-lg p-3 bg-amber-50/50" data-testid="email-previews-panel">
                <h4 className="text-sm font-bold text-gray-700 mb-2">📨 Customer को जाने वाले सभी Emails</h4>
                {emailPreviews.length === 0 ? (
                  <div className="text-xs text-gray-400 py-2">Loading previews...</div>
                ) : emailPreviews.map((p) => (
                  <div key={p.key} className="mb-2 bg-white rounded-lg border border-gray-200">
                    <button onClick={() => setOpenPreviewKey(openPreviewKey === p.key ? null : p.key)}
                      className="w-full text-left p-3 flex items-center justify-between" data-testid={`email-preview-btn-${p.key}`}>
                      <div>
                        <div className="text-sm font-bold text-gray-800">{p.title}</div>
                        <div className="text-xs text-gray-500 mt-0.5">Subject: {p.subject}</div>
                      </div>
                      <span className="text-gray-400 text-xs">{openPreviewKey === p.key ? '▲ बंद करें' : '▼ देखें'}</span>
                    </button>
                    {openPreviewKey === p.key && (
                      <iframe title={p.key} srcDoc={p.html} sandbox="" className="w-full border-t border-gray-200 rounded-b-lg bg-white" style={{ height: '520px' }} data-testid={`email-preview-frame-${p.key}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
            {emailLogsLoading ? (
              <div className="text-center py-6 text-gray-400">Loading...</div>
            ) : emailLogs.length === 0 ? (
              <div className="text-center py-6 text-gray-400">अभी कोई email log नहीं है</div>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="text-left text-gray-500 text-xs uppercase">
                      <th className="p-2">समय</th><th className="p-2">To</th><th className="p-2">Subject</th><th className="p-2">Service</th><th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map((log, i) => (
                      <tr key={log.id || i} className="border-t border-gray-100" data-testid={`email-log-row-${i}`}>
                        <td className="p-2 text-gray-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString('hi-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="p-2 font-semibold text-gray-800">{log.to}</td>
                        <td className="p-2 text-gray-600 max-w-xs truncate" title={log.subject}>{log.subject}</td>
                        <td className="p-2 text-gray-500">{log.service}</td>
                        <td className="p-2">
                          {log.status === 'sent'
                            ? <span className="text-green-600 font-bold">✓ Sent{log.opened ? <span className="text-blue-600"> · 👁 Opened</span> : null}</span>
                            : <span className="text-red-600 font-bold" title={log.error}>✗ Failed</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* WhatsApp Chat Panel */}
        {showConsults && <ConsultationBookingsPanel />}
        {showWaApi && <WaApiChatPanel />}
        {showBookings && <BookingsPanel />}
        {showFailed && <FailedMsgPanel />}

        {/* Leads - Card Layout */}
        {!loading && !showEmails && !showBookings && !showFailed && !showConsults && !showWaApi && (
          <div className="space-y-3" data-testid="leads-list">
            {filteredLeads.map((lead, i) => (
              <LeadCard key={lead.id || i} lead={lead} index={i}
                isExpanded={expandedLead === (lead.id || i)}
                onToggle={() => toggleExpand(lead.id || i, lead.mobile1, lead.mobile2)}
                onUpdateStatus={updateStatus} statusUpdating={statusUpdating[lead.id]}
                phoneLookups={phoneLookups} lookupLoading={lookupLoading}
                retryCounts={retryCounts} wa1Number={stats.wa1_number} formatDate={formatDate}
                note={notes[lead.id] || lead.admin_note || ''}
                setNote={(val) => setNotes(prev => ({ ...prev, [lead.id]: val }))}
                saveNote={(val) => saveNote(lead.id, val)}
                noteSaving={noteSaving[lead.id]}
                sendEmail={sendEmail}
                emailSending={emailSending}
                openEmailThread={() => setEmailThreadLead({ id: lead.id, name: lead.name })}
                sendOffer={sendOffer} offerSending={offerSending}
                placeCall={placeCall} calling={callingLead[lead.id]}
              />
            ))}
            {filteredLeads.length === 0 && <div className="text-center py-12 text-gray-400 text-base bg-white rounded-xl shadow">कोई lead नहीं मिला</div>}
          </div>
        )}
      </div>
      {emailThreadLead && (
        <EmailThreadModal leadId={emailThreadLead.id} leadName={emailThreadLead.name}
          onClose={() => setEmailThreadLead(null)} />
      )}
    </div>
  );
};

const StatBox = ({ value, label, color }) => (
  <div className={`${color} text-white rounded-xl p-2 sm:p-3 text-center shadow-lg`}>
    <div className="text-lg sm:text-2xl font-black leading-none">{value}</div>
    <div className="text-[9px] sm:text-xs font-bold opacity-90 mt-1 leading-tight">{label}</div>
  </div>
);

const LeadCard = ({ lead, index, isExpanded, onToggle, onUpdateStatus, statusUpdating, phoneLookups, lookupLoading, retryCounts, wa1Number, formatDate, note, setNote, saveNote, noteSaving, sendEmail, emailSending, openEmailThread, placeCall, calling, sendOffer, offerSending }) => {
  const currentStatus = lead.call_status || '';
  const statusConf = STATUS_CONFIG[currentStatus];
  const [custCalled, setCustCalled] = React.useState(!!lead.customer_called);
  const [custCalledSaving, setCustCalledSaving] = React.useState(false);

  const toggleCustomerCalled = async (e) => {
    e.stopPropagation();
    setCustCalledSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/contacts/${lead.id}/customer-called`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ called: !custCalled })
      });
      const data = await res.json();
      if (data.success) setCustCalled(!custCalled);
    } catch (err) { console.error('customer-called update failed', err); }
    setCustCalledSaving(false);
  };

  return (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden ${isExpanded ? 'ring-2 ring-blue-500' : ''}`} data-testid={`lead-row-${index}`}>
      {/* Main Card Content */}
      <div className="p-4">
        {/* Row 1: Index, Name, Status */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
            <span className="bg-blue-100 text-blue-700 text-sm font-black w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">{index + 1}</span>
            <span className="text-base sm:text-lg font-black text-gray-900 break-words" data-testid={`lead-name-${index}`}>{lead.name}</span>
            {lead.chat_status === 'offer_made' && lead.payment_status !== 'paid' && (
              <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-yellow-100 text-yellow-800 border border-yellow-300 flex-shrink-0" data-testid="parchi-pending-badge">
                🟡 {lead.chat_last_offer?.offer_type === 'kundali' ? 'कुंडली ₹299' : 'पर्ची ₹100'} pending
              </span>
            )}
            {SEWA_DAL[lead.whatsapp_target] && (
              <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200 flex-shrink-0" data-testid="sewa-dal-badge">
                📞 {SEWA_DAL[lead.whatsapp_target]}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 flex-wrap">
            <button
              onClick={toggleCustomerCalled}
              disabled={custCalledSaving}
              data-testid={`customer-called-btn-${lead.id}`}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold disabled:opacity-50 border-2 ${custCalled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-500 border-gray-300 hover:border-emerald-400'}`}
              title="क्या customer ने खुद call किया? (toggle)"
            >{custCalledSaving ? '⏳' : custCalled ? '📲 Customer ने Call किया ✓' : '📵 Call नहीं आया'}</button>
            <button
              onClick={(e) => { e.stopPropagation(); sendOffer(lead.id, 'consult_call_5100'); }}
              disabled={offerSending[lead.id + 'consult_call_5100']}
              data-testid={`offer-call-btn-${lead.id}`}
              className="px-2 sm:px-2.5 py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
              title="माता जी से ₹5100 की 30-min call का offer WhatsApp पर भेजें"
            >{offerSending[lead.id + 'consult_call_5100'] ? '⏳' : '📱 ₹5100 Call'}</button>
            <button
              onClick={(e) => { e.stopPropagation(); sendOffer(lead.id, 'personal_visit_51000'); }}
              disabled={offerSending[lead.id + 'personal_visit_51000']}
              data-testid={`offer-visit-btn-${lead.id}`}
              className="px-2.5 py-1 rounded-lg text-xs font-bold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              title="₹51000 की व्यक्तिगत भेंट का offer WhatsApp पर भेजें"
            >{offerSending[lead.id + 'personal_visit_51000'] ? '⏳' : '🏛 ₹51000 Visit'}</button>
            <button
              onClick={(e) => { e.stopPropagation(); placeCall(lead.id); }}
              disabled={calling}
              data-testid={`ai-call-btn-${lead.id}`}
              className="px-3 py-1 rounded-lg text-sm font-bold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
              title="AI सेविका से कॉल करवाएँ"
            >
              {calling ? '⏳' : '📞'} {lead.ai_call_status === 'completed' ? 'Call ✓' : lead.ai_call_status === 'initiated' || lead.ai_call_status === 'calling' ? 'Calling…' : lead.ai_call_status === 'scheduled' ? '⏰ 15min' : 'AI Call'}
            </button>
            {statusConf ? (
              <span className={`px-3 py-1 rounded-lg text-sm font-bold ${statusConf.bg} ${statusConf.text}`}>{statusConf.label}</span>
            ) : (
              <span className="px-3 py-1 rounded-lg text-sm font-bold bg-gray-200 text-gray-700">Pending</span>
            )}
          </div>
        </div>

        {/* Row 2: Mobile Numbers */}
        <div className="flex flex-wrap gap-2 mb-3">
          <PhoneButton number={lead.mobile1} label="M1" />
          <PhoneButton number={lead.mobile2} label="M2" />
          <span className={`px-3 py-1.5 rounded-lg text-sm font-bold ${lead.whatsapp_target === wa1Number ? 'bg-purple-100 text-neutral-700 border-2 border-neutral-200' : 'bg-orange-100 text-orange-700 border-2 border-orange-200'}`}>
            WA: {lead.whatsapp_target || '-'}
          </span>
        </div>

        {/* Row 3: Problem - FULL TEXT */}
        <div className="bg-blue-50 rounded-xl p-3 mb-3 border-2 border-blue-100">
          <div className="text-xs text-blue-600 font-bold mb-1">📝 समस्या:</div>
          <div className="text-base text-gray-800 font-medium leading-relaxed">{lead.problem || '-'}</div>
        </div>

        {/* Row 4: Date + Expand Button */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 font-medium">◈ {formatDate(lead.created_at)}</span>
          <button onClick={onToggle} data-testid={`lead-expand-${index}`} className={`px-4 py-2 text-sm font-bold rounded-lg ${isExpanded ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {isExpanded ? '▲ बंद करें' : '▼ Details + Note'}
          </button>
        </div>
      </div>

      {/* Expanded Section */}
      {isExpanded && (
        <div className="border-t-2 border-gray-100 bg-gray-50 p-4">
          {/* AI Call Result */}
          {(lead.call_summary || lead.ai_call_status) && (
            <div className="mb-4 bg-green-50 rounded-xl p-4 border-2 border-green-200" data-testid={`ai-call-result-${index}`}>
              <div className="text-sm font-bold text-green-700 mb-2">
                📞 AI Call: <span className="uppercase">{lead.ai_call_status || '-'}</span>
                {lead.call_duration_sec ? ` • ${lead.call_duration_sec}s` : ''}
                {lead.call_analysis?.sentiment ? ` • ${lead.call_analysis.sentiment}` : ''}
              </div>
              {lead.ai_call_error && lead.ai_call_status === 'failed' && (
                <div className="text-xs text-red-600 mb-2" data-testid={`ai-call-error-${index}`}><b>Error:</b> {lead.ai_call_error}</div>
              )}
              {lead.call_summary && (
                <div className="text-sm text-gray-800 mb-2"><b>Summary:</b> {lead.call_summary}</div>
              )}
              {lead.call_recording_url && (
                <a href={lead.call_recording_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline font-bold">🎧 Recording सुनें</a>
              )}
              {lead.call_transcript && (
                <details className="mt-2">
                  <summary className="text-sm font-bold text-green-700 cursor-pointer">Transcript देखें</summary>
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap mt-2 max-h-48 overflow-y-auto bg-white p-2 rounded-lg border">{lead.call_transcript}</pre>
                </details>
              )}
            </div>
          )}
          {/* Note Section */}
          <div className="mb-4 bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
            <div className="text-sm font-bold text-yellow-700 mb-2">📝 Admin Note:</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="यहाँ note लिखें..."
              className="w-full p-3 text-base border-2 border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-400 resize-none"
              rows={2}
            />
            <button 
              onClick={() => saveNote(note)}
              disabled={noteSaving}
              className="mt-2 px-4 py-2 bg-yellow-500 text-white text-sm font-bold rounded-lg hover:bg-yellow-600 disabled:opacity-50"
            >
              {noteSaving ? 'Saving...' : '💾 Save Note'}
            </button>
          </div>

          {/* Status Buttons */}
          <div className="mb-4 bg-white rounded-xl p-4 border-2 border-gray-200">
            <div className="text-sm font-bold text-gray-600 mb-2">🔄 STATUS UPDATE करें:</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, conf]) => (
                <button key={key} onClick={() => onUpdateStatus(lead.id, key)} disabled={statusUpdating}
                  className={`px-4 py-2 text-sm font-bold rounded-lg transition ${currentStatus === key ? conf.bg + ' ' + conf.text + ' ring-2 ring-offset-2 ring-gray-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${statusUpdating ? 'opacity-50' : ''}`}
                  data-testid={`status-btn-${key}-${index}`}>{conf.label}</button>
              ))}
            </div>
          </div>

          {/* Report Page + WhatsApp Actions */}
          <div className="mb-4 bg-white rounded-xl p-4 border-2 border-gray-200" data-testid={`report-actions-${index}`}>
            <div className="text-sm font-bold text-gray-600 mb-2">📄 REPORT actions:</div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`/report/${lead.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`btn-view-report-${index}`}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg shadow inline-flex items-center gap-2"
              >
                📋 Report Page देखें
              </a>
              <button
                onClick={() => {
                  const reportLink = `${window.location.origin}/report/${lead.id}`;
                  const msg = `नमस्ते ${lead.name} जी 🙏\n\nमाता मलिहा जी ने आपकी समस्या की पूरी रिपोर्ट तैयार कर ली है।\n\n📋 अपनी रिपोर्ट यहाँ देखें:\n${reportLink}\n\n— माता मलिहा सेवा`;
                  const wa = `https://wa.me/91${lead.mobile1}?text=${encodeURIComponent(msg)}`;
                  window.open(wa, '_blank');
                }}
                data-testid={`btn-wa-report-${index}`}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg shadow inline-flex items-center gap-2"
              >
                💬 WhatsApp पर Report भेजें
              </button>
              <button
                onClick={() => {
                  const reportLink = `${window.location.origin}/report/${lead.id}`;
                  navigator.clipboard.writeText(reportLink);
                  alert(`✓ Report link copy हो गया:\n${reportLink}`);
                }}
                data-testid={`btn-copy-report-${index}`}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-bold rounded-lg shadow inline-flex items-center gap-2"
              >
                🔗 Link Copy
              </button>
            </div>
            <div className="text-xs text-gray-400 mt-2">
              WhatsApp भेजते समय message छोटा रखा गया है ताकि number block न हो।
            </div>
          </div>

          {/* Payment Link Actions */}
          <div className="mb-4 bg-white rounded-xl p-4 border-2 border-amber-300" data-testid={`payment-actions-${index}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-sm font-bold text-amber-700">💳 PAYMENT LINK (₹5500 — Razorpay):</div>
              <button
                onClick={openEmailThread}
                data-testid={`btn-email-thread-${index}`}
                className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg shadow inline-flex items-center gap-1"
              >
                📧 Email Conversation {lead.email_thread?.length ? `(${lead.email_thread.length})` : ''}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/payment/create-link/${lead.id}`, { method: 'POST' });
                    const data = await res.json();
                    if (data.success && data.short_url) {
                      await navigator.clipboard.writeText(data.short_url);
                      alert(`✓ Payment link copy हो गया:\n${data.short_url}`);
                    } else {
                      alert('Error: ' + (data.error || 'Failed to create link'));
                    }
                  } catch (e) { alert('Failed: ' + e.message); }
                }}
                data-testid={`btn-copy-payment-link-${index}`}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold rounded-lg shadow inline-flex items-center gap-2"
              >
                🔗 Copy Payment Link
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/payment/create-link/${lead.id}`, { method: 'POST' });
                    const data = await res.json();
                    if (data.success && data.short_url) {
                      const msg = `नमस्ते ${lead.name} जी 🙏\n\nमाता मलिहा जी की साधना के लिए दक्षिणा भुगतान:\n\n💳 ₹5500 (₹4400 सामग्री + ₹1100 भोग)\n${data.short_url}\n\nभुगतान के बाद screenshot भेजें।\n\n— जय माता मलिहा`;
                      const wa = `https://wa.me/91${lead.mobile1}?text=${encodeURIComponent(msg)}`;
                      window.open(wa, '_blank');
                    } else {
                      alert('Error: ' + (data.error || 'Failed to create link'));
                    }
                  } catch (e) { alert('Failed: ' + e.message); }
                }}
                data-testid={`btn-wa-payment-link-${index}`}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg shadow inline-flex items-center gap-2"
              >
                💬 WhatsApp पर Payment Link भेजें
              </button>
            </div>
            <div className="text-xs text-amber-600 mt-2">
              Click करते ही Razorpay पर ₹5500 का link auto-generate होगा। Customer को सीधा भेजकर payment ले सकते हैं।
            </div>
          </div>
          <div className="mb-4 bg-white rounded-xl p-4 border-2 border-gray-200" data-testid={`email-actions-${index}`}>
            <div className="text-sm font-bold text-gray-600 mb-2">📧 EMAIL भेजें (1-click):</div>
            {!lead.email ? (
              <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">⚠ Email नहीं है इस lead के लिए</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => sendEmail(lead.id, 'instant')}
                  disabled={emailSending[`${lead.id}-instant`]}
                  data-testid={`btn-send-instant-${index}`}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg shadow disabled:opacity-50"
                >
                  {emailSending[`${lead.id}-instant`] ? '...' : '📩 Instant Confirmation'}
                </button>
                <button
                  onClick={() => sendEmail(lead.id, 'report')}
                  disabled={emailSending[`${lead.id}-report`]}
                  data-testid={`btn-send-report-${index}`}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold rounded-lg shadow disabled:opacity-50"
                >
                  {emailSending[`${lead.id}-report`] ? '...' : '📋 Detailed Report'}
                </button>
                <button
                  onClick={() => sendEmail(lead.id, 'payment')}
                  disabled={emailSending[`${lead.id}-payment`]}
                  data-testid={`btn-send-payment-${index}`}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg shadow disabled:opacity-50"
                >
                  {emailSending[`${lead.id}-payment`] ? '...' : '✓ Payment Confirmation'}
                </button>
                <span className="text-xs text-gray-500 px-2 py-2">→ {lead.email}</span>
              </div>
            )}
            <div className="text-xs text-gray-400 mt-2">
              Status: Instant {lead.instant_email_sent ? '✓' : '✗'} | Report {lead.report_email_sent ? '✓' : '✗'}
            </div>
          </div>

          {/* Phone Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PhoneDetailsCard label="Mobile 1" number={lead.mobile1} leadId={lead.id} data={phoneLookups[lead.mobile1]} loading={lookupLoading[lead.mobile1]} retryCount={retryCounts[lead.mobile1] || 0} />
            <PhoneDetailsCard label="Mobile 2" number={lead.mobile2} leadId={lead.id} data={phoneLookups[lead.mobile2]} loading={lookupLoading[lead.mobile2]} retryCount={retryCounts[lead.mobile2] || 0} />
          </div>

          {/* Chat History Viewer */}
          <ChatHistoryViewer leadId={lead.id} chatStatus={lead.chat_status} testIdSuffix={index} />
        </div>
      )}
    </div>
  );
};

const ChatHistoryViewer = ({ leadId, chatStatus, testIdSuffix }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const load = async () => {
    if (open) { setOpen(false); return; }
    if (!fetched) {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/chat/${leadId}`);
        const d = await res.json();
        setMessages(d.messages || []);
      } catch (e) { console.error('chat load failed:', e); }
      setLoading(false);
      setFetched(true);
    }
    setOpen(true);
  };

  const statusLabel = {
    active: '💬 Chat चल रहा है',
    offer_made: '💰 दक्षिणा offer भेजी गई',
    declined: '⚠ Customer ने मना किया',
    not_started: '⏸ Chat शुरू नहीं हुआ'
  }[chatStatus || 'not_started'] || chatStatus;

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('hi-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="mt-4 bg-white rounded-xl p-4 border-2 border-purple-200" data-testid={`chat-history-${testIdSuffix}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-purple-700">🕉 माता जी की Chat History</div>
          <div className="text-xs text-gray-500 mt-0.5">{statusLabel}</div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid={`chat-history-btn-${testIdSuffix}`}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg shadow disabled:opacity-50"
        >
          {loading ? '...' : (open ? '▲ छिपाएं' : '▼ पूरी बातचीत देखें')}
        </button>
      </div>

      {open && (
        <div className="mt-3">
          {messages.length === 0 && (
            <div className="text-sm text-gray-500 italic p-4 text-center bg-gray-50 rounded-lg">
              अभी तक Customer ने chat शुरू नहीं की है
            </div>
          )}
          <div className="space-y-2 max-h-[400px] overflow-y-auto bg-gradient-to-b from-[#F5EBD9] to-[#FFF8EB] rounded-lg p-3">
            {messages.map((m, i) => {
              const isBot = m.role === 'assistant';
              return (
                <div key={i} className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${isBot ? 'bg-white border border-purple-200 rounded-tl-none' : 'bg-[#5B1818] text-[#FBF6E9] rounded-tr-none'}`}>
                    <div className="text-[10px] font-bold opacity-70 mb-0.5">
                      {isBot ? '🕉 माता जी' : '👤 भक्त'} • {fmtTime(m.at)}
                    </div>
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    {isBot && m.payment_offer && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded text-xs">
                        💰 <b>Offer भेजा:</b> {m.payment_offer.tier} — ₹{m.payment_offer.amount}
                      </div>
                    )}
                    {m.stage && (
                      <div className="text-[10px] opacity-60 mt-1 italic">Stage: {m.stage}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const FamilyDetails = ({ aadhar, leadId, testIdSuffix }) => {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [fetched, setFetched] = useState(false);

  const fetchFamily = async () => {
    setLoading(true);
    setErr('');
    try {
      const url = leadId ? `${API_URL}/api/family-lookup/${aadhar}?lead_id=${leadId}` : `${API_URL}/api/family-lookup/${aadhar}`;
      const res = await fetch(url);
      const d = await res.json();
      if (d.error || !d.status) {
        setErr('अतिरिक्त जानकारी उपलब्ध नहीं');
      } else {
        setData(d);
        setOpen(true);
      }
    } catch (e) { setErr('अतिरिक्त जानकारी उपलब्ध नहीं'); }
    setLoading(false);
    setFetched(true);
  };

  // Auto-fetch on mount when aadhar is available
  useEffect(() => {
    if (aadhar && !fetched && !loading) {
      fetchFamily();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aadhar]);

  const memberNames = (data?.members || [])
    .map(m => (m.member_name || m.name || '').trim())
    .filter(Boolean);

  return (
    <div className="mt-2 pt-2 border-t border-gray-200" data-testid={`family-${testIdSuffix}`}>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-purple-600">
          <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <span>बंधन के अन्य स्रोत खोजे जा रहे हैं...</span>
        </div>
      )}
      {err && !loading && <div className="text-gray-500 text-xs italic">{err}</div>}
      {!loading && data && (
        <div className="mt-1 bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs">
          {/* All member names together — prominent display at top */}
          {memberNames.length > 0 && (
            <div className="mb-2 pb-2 border-b border-purple-200">
              <div className="font-bold text-purple-800 mb-1">🪔 बंधन के सभी सदस्य ({memberNames.length}):</div>
              <div className="text-purple-900 font-semibold leading-relaxed" data-testid={`family-all-names-${testIdSuffix}`}>
                {memberNames.join(' • ')}
              </div>
            </div>
          )}

          {/* Family address */}
          {data.card_info && (data.card_info.District || data.card_info.State || data.card_info.Address) && (
            <div className="mb-2 pb-2 border-b border-purple-200">
              <div className="font-bold text-purple-700 mb-1">पारिवारिक स्थान</div>
              {data.card_info.District && <div><b>क्षेत्र:</b> {data.card_info.District}, {data.card_info.State}</div>}
              {data.card_info.Address && <div><b>केंद्र:</b> {data.card_info.Address}</div>}
            </div>
          )}

          {/* Toggle for detailed member info */}
          {(data.members || []).length > 0 && (
            <>
              <button
                onClick={() => setOpen(o => !o)}
                className="text-purple-600 hover:text-purple-800 text-xs font-bold mb-1"
                data-testid={`family-toggle-${testIdSuffix}`}
              >
                {open ? '▼ विस्तृत जानकारी छिपाएं' : '▶ विस्तृत जानकारी देखें'}
              </button>
              {open && (
                <div>
                  {(data.members || []).map((m, i) => (
                    <div key={i} className="bg-white rounded p-2 mb-1 border border-purple-100">
                      <div><b className="text-purple-700">{m.member_name || m.name}</b> {m.relationship && <span className="text-gray-500">({m.relationship})</span>}</div>
                      {m.gender && <div className="text-gray-600">लिंग: {m.gender === 'F' ? 'महिला' : m.gender === 'M' ? 'पुरुष' : m.gender}</div>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PhoneButton = ({ number, label }) => {
  if (!number) return <span className="text-sm text-gray-300">{label}: -</span>;
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-gray-500 font-bold">{label}:</span>
      <a href={`tel:+91${number}`} className="px-3 py-1.5 bg-blue-100 text-blue-700 text-sm font-bold rounded-lg border-2 border-blue-200 hover:bg-blue-200">{number}</a>
      <a href={`https://wa.me/91${number}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg border-2 border-green-200 hover:bg-green-200">💬</a>
    </div>
  );
};

const PhoneDetailsCard = ({ label, number, leadId, data, loading, retryCount }) => {
  if (!number) return (
    <div className="bg-white rounded-xl p-4 border-2 border-gray-200">
      <div className="text-sm font-bold text-gray-400">{label}: नंबर नहीं दिया गया</div>
    </div>
  );

  const hasData = data?.status && data?.data?.length > 0 && data.data.some(e => e.name || e.father_name || e['father name'] || e.fname || e.address);

  return (
    <div className="bg-white rounded-xl p-4 border-2 border-gray-200" data-testid={`phone-details-${number}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-gray-100">
        <span className="text-base font-bold text-blue-700">{label}: {number}</span>
        <div className="flex gap-2">
          <a href={`tel:+91${number}`} className="px-3 py-1.5 bg-blue-100 text-blue-700 text-sm font-bold rounded-lg hover:bg-blue-200">☎ Call</a>
          <a href={`https://wa.me/91${number}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-green-100 text-green-700 text-sm font-bold rounded-lg hover:bg-green-200">💬 WA</a>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center gap-3 py-3">
          <div className="w-5 h-5 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-500 text-sm font-medium">Loading...</span>
        </div>
      )}

      {/* Data Found - shows full lookup details + family members from FAMILY API */}
      {!loading && hasData && (
        <div className="space-y-2">
          {data.data.filter(e => e.name || e.father_name || e['father name'] || e.fname || e.address).slice(0, 3).map((entry, idx) => {
            const aadhar = entry.id_number || entry.aadhar || entry.uid || '';
            return (
              <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm border border-gray-100">
                {entry.summary_line && (
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-900 font-semibold" data-testid={`lookup-summary-${number}-${idx}`}>
                    {entry.summary_line}
                  </div>
                )}
                {aadhar && (
                  <FamilyDetails aadhar={aadhar} leadId={leadId} testIdSuffix={`${number}-${idx}`} />
                )}
              </div>
            );
          })}
          <div className="text-green-600 text-sm font-bold mt-2">✓ जानकारी सहेजी गई</div>
        </div>
      )}

      {/* No Data - permanently checked, no retries */}
      {!loading && !hasData && data && (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="text-gray-500 text-sm italic">अतिरिक्त जानकारी उपलब्ध नहीं</div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
